<script setup lang="ts">
import type { PageHeaderMenuItem } from '~/components/page/header/menu/PageHeaderMenu.vue'
import type { PageHeaderLinkItem } from '~/components/page/header/links/PageHeaderLinks.vue'
import { SrcSet } from '~/composables/useSrcSet'
// @ts-ignore
// @ts-ignore
import { TrackerDataType } from '@scr-npm/nuxt-tracker/dist/runtime/types'

export interface PageHeaderProps {
    title: string | null;
    subtitle?: string;
    backgroundImage?: string;
    sections?: PageHeaderMenuItem[];
    cta?: PageHeaderCTA;
    links?: PageHeaderLinkItem[];
    size?: PageHeaderSize;
    perex?: PageHeaderPerex;
    headline: PageHeaderHeadline;
    roundedTop?: any;
    ctaScrollTarget?: string;
    headingType?: PageHeaderHeadingType;
}

const props = defineProps<PageHeaderProps>()
const observer = useObserver({ threshold: 0.28, rootMargin: '0px' })
const wrapperElement = ref<HTMLElement>()
const pageHeaderButtonElement = ref<HTMLButtonElement | HTMLAnchorElement>()
const pageHeaderButtonIsFixed = useState<boolean>(
    'pageHeaderButtonIsFixed',
    (): boolean => false
)

export interface PageHeaderEmits {
    (e: 'active-section', value: boolean): void;
}

const emit = defineEmits<PageHeaderEmits>()

const desktopSrcSet = computed((): SrcSet | undefined =>
    useSrcSet(props.headline.image?.path)
)
const mobileSrcSet = computed((): SrcSet | undefined =>
    useSrcSet(props.headline.image_mobile?.path)
)

const ctaLinkUrl = computed<VNuxtLinkTo>(
    (): VNuxtLinkTo => useCtaLinkUrl(props.cta)
)

const handleClickOnScrollToTarget = (): void => {
    // only on client side
    if (!import.meta.client || !props.ctaScrollTarget) return
    // get target section
    const target = document.querySelector(props.ctaScrollTarget) as HTMLElement
    if (!target) return
    // scroll to the target
    useScrollIntoView(target)

    // Move focus to heading inside target for screen reader users (WCAG 2.4.3)
    // This announces the section title to screen reader users
    // Use longer timeout to ensure lazy-loaded content is rendered
    const moveFocusToTarget = () => {
        // Find the first heading inside the target section
        const heading = target.querySelector('h1, h2, h3, h4, h5, h6') as HTMLElement
        const focusTarget = heading || target

        // Make element focusable if it isn't already
        if (!focusTarget.hasAttribute('tabindex')) {
            focusTarget.setAttribute('tabindex', '-1')
        }
        focusTarget.focus({ preventScroll: true })
    }

    // Try immediately, then retry after delay for lazy-loaded content
    setTimeout(moveFocusToTarget, 100)
    setTimeout(moveFocusToTarget, 800)
}

const handleIntersection = (isIntersecting: boolean): void => {
    if (!isIntersecting) {
        wrapperElement.value?.classList.add('is-hidden')
        emit('active-section', true)

        if (pageHeaderButtonElement.value && window.innerWidth < 768) {
            pageHeaderButtonElement.value?.classList.add('fixed-bottom')
            pageHeaderButtonIsFixed.value = true
        }
        return
    }

    if (pageHeaderButtonElement.value?.classList.contains('fixed-bottom')) {
        pageHeaderButtonElement.value?.classList.remove('fixed-bottom')
        pageHeaderButtonIsFixed.value = false
    }

    wrapperElement.value?.classList.remove('is-hidden')
    emit('active-section', false)
}

onMounted(() => {
    if (!wrapperElement.value) return
    observer(wrapperElement.value, handleIntersection)
})

useHead({
    link: [
        {
            rel: 'preload',
            as: 'image',
            href: desktopSrcSet.value?.src,
            imagesrcset: desktopSrcSet.value?.srcSet,
            media: '(min-width: 768px)',
            tagPriority: 'high',
            fetchpriority: 'high',
        },
        {
            rel: 'preload',
            as: 'image',
            href: mobileSrcSet.value?.src,
            imagesrcset: mobileSrcSet.value?.srcSet,
            media: '(max-width: 767px)',
            tagPriority: 'high',
            fetchpriority: 'high',
        },
    ],
})
</script>

<template>
  <div
    ref="wrapperElement"
    class="position-relative page-header-wrapper"
  >
    <Tracker :data="{ position: props.title } as TrackerDataType">
      <header
        class="page-header position-relative px-3 d-flex justify-content-center align-items-center overflow-hidden"
        :class="[
          `page-header-size-${props.size || 'normal'}`,
          { 'mb-lg-20': props.sections },
          { 'mb-md-6 mb-lg-0': !props.sections },
          { 'rounded-top-0': roundedTop === false },
        ]"
      >
        <div class="headline-sticker-wrapper" />
        <picture class="position-absolute w-100 h-100">
          <source
            v-if="mobileSrcSet"
            media="(max-width: 767px)"
            :srcset="mobileSrcSet?.srcSet"
            width="768"
            height="510"
          >
          <source
            v-if="desktopSrcSet"
            media="(min-width: 768px)"
            :srcset="desktopSrcSet?.srcSet"
            width="1540"
            height="430"
          >
          <img
            v-if="desktopSrcSet"
            :src="desktopSrcSet.src"
            :srcset="desktopSrcSet?.srcSet"
            width="1540"
            height="430"
            class="w-100 h-100 object-fit-cover"
            alt=""
          >
        </picture>
        <div class="flex-grow-1 position-relative z-index-5">
          <slot name="image" />
          <PageHeaderContent
            :tag="props.headline.tag"
            :title="props.title"
            :heading-type="props.headingType"
            :subtitle="props.subtitle"
            :cta-title="props.headline.cta_title"
            :cta-scroll-target="props.ctaScrollTarget"
          >
            <template #cta>
              <div
                v-if="props.cta"
                ref="pageHeaderButtonElement"
                class="d-flex justify-content-center"
              >
                <!-- When ctaScrollTarget is provided, render as button (not link) since it scrolls instead of navigating -->
                <template v-if="props.ctaScrollTarget">
                  <!-- Mobile button (can become fixed-bottom) -->
                  <VButton
                    class="mt-8 mt-md-3 d-md-none"
                    color="primary"
                    size="large"
                    type="button"
                    :label="props.cta.text"
                    @click="handleClickOnScrollToTarget"
                  >
                    {{ props.cta.text }}
                  </VButton>
                  <!-- Desktop button -->
                  <VButton
                    class="mt-8 mt-md-3 d-none d-md-inline-flex"
                    color="primary"
                    size="large"
                    type="button"
                    :label="props.cta.text"
                    @click="handleClickOnScrollToTarget"
                  >
                    {{ props.cta.text }}
                  </VButton>
                </template>
                <!-- When no scroll target, render as link for actual navigation -->
                <VButton
                  v-else
                  :target="
                    props.cta.isExternal
                      ? '_blank'
                      : '_self'
                  "
                  class="mt-8 mt-md-3 d-lg-none"
                  color="primary"
                  size="large"
                  :to="ctaLinkUrl"
                  :label="props.cta.text"
                >
                  {{ props.cta.text }}
                </VButton>
              </div>
            </template>
          </PageHeaderContent>
        </div>
      </header>

      <PageHeaderPerex
        v-if="props.perex"
        :cta="props.perex.cta"
        :description="props.perex.content"
      />

      <PageHeaderLinks
        v-if="props.links"
        :data="props.links"
      />

      <PageHeaderMenu
        v-if="props.sections?.length"
        :cta="props.cta"
        :data="props.sections"
      />
    </Tracker>
  </div>
</template>

<style>
@import "./PageHeader.vars.scss";
</style>

<style scoped lang="scss">
@import "./PageHeader.scss";
</style>
